/**
 * 跨站语言同步的 URL 参数（web-onboarding 同口径）：官网、开户站、用户中心三者互跳时都带 `lang`，
 * 取值只认 `zh-CN` / `zh-HK` / `en-AU` 三个（与官网同事 2026-10-13 约定，不做大小写或别名放宽）。
 *
 * 这里只放纯函数，不碰 i18n / localStorage，便于单测；接线在 `locales/index.ts`。
 * web-onboarding 有一份相同的文件（两仓独立部署，不共享代码），改一处要同步另一处。
 */

export const LANG_PARAM = 'lang'

const ALLOWED = ['zh-CN', 'zh-HK', 'en-AU'] as const
export type LangParam = (typeof ALLOWED)[number]

/** 从 `location.search` 读语言；没有或不是三个合法值之一 → null */
export function readLangParam(search: string): LangParam | null {
  const v = new URLSearchParams(search).get(LANG_PARAM)
  return v && (ALLOWED as readonly string[]).includes(v) ? (v as LangParam) : null
}

/**
 * 去掉地址里的 `lang`（其余参数与 hash 原样保留）；没有 `lang` 返回 null。
 * 合法与否都去掉：参数只在进站那一刻生效，留在地址栏里，刷新会把客户后来在站内的切换覆盖回去。
 */
export function stripLangParam(href: string): string | null {
  const u = new URL(href)
  if (!u.searchParams.has(LANG_PARAM)) return null
  u.searchParams.delete(LANG_PARAM)
  return u.pathname + u.search + u.hash
}

/** 给出站链接带上语言：已有 `lang` 就替换，其余参数与 hash 不动 */
export function appendLang(url: string, lang: LangParam): string {
  const hashAt = url.indexOf('#')
  const hash = hashAt < 0 ? '' : url.slice(hashAt)
  const rest = hashAt < 0 ? url : url.slice(0, hashAt)
  const qAt = rest.indexOf('?')
  const path = qAt < 0 ? rest : rest.slice(0, qAt)
  const params = new URLSearchParams(qAt < 0 ? '' : rest.slice(qAt + 1))
  params.set(LANG_PARAM, lang)
  return `${path}?${params.toString()}${hash}`
}
